import { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { request } from '@/renderer/utils/api';
import type { RequestSchema } from '@/types/request';

// Delivery-coverage TREND (#1473) — the companion to the point-in-time coverage
// panel (#1274). It plots the daily snapshot history so admins can tell whether
// the no-channel email nudge (#1381) is working.
//
// KEEPING THE TREND HONEST: a drop in coverage % is often just subscriber GROWTH
// (new paid subs start unreachable until they set up a channel), not the reminders
// failing. So the % alone is misleading. This chart plots THREE series together:
//   • Coverage %      (left axis, emerald)  — the headline, with the 90% goal line
//   • Reachable       (right axis, indigo)  — the ABSOLUTE count reachable by push
//   • Active subs      (right axis, zinc)    — the total paid base (denominator)
// The count axis is zero-based, so the gap between "Active subs" and "Reachable"
// is the unreachable group at true scale. When % dips while Reachable keeps
// RISING and Active subs rises faster, that's growth outpacing setup — NOT the
// nudge failing. When % dips AND Reachable is flat/falling, that's a real problem.

const HISTORY_ENDPOINT = '/api/v1.1/admin/stats/coverage/history';

// Line colors — explicit hex so they read in both light and dark (matching the
// EMERALD convention in results/components/AreaChart.tsx). Axes/grid use
// currentColor + low opacity and inherit the container's theme text color.
const EMERALD = '#10b981'; // coverage %
const INDIGO = '#6366f1'; // reachable count
const ZINC = '#a1a1aa'; // total active subs (base)

/** Fetch the coverage snapshot history. Read-only; fails quiet (returns null). */
export function useCoverageHistory(days = 90) {
  const [history, setHistory] = useState<API.CoverageHistory | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    request(`${HISTORY_ENDPOINT}?days=${days}` as unknown as RequestSchema<API.CoverageHistory>)
      .then((res) => setHistory((res as any) ?? null))
      .catch(() => setHistory(null))
      .finally(() => setLoading(false));
  }, [days]);
  return { history, loading };
}

/** "2026-08-03" → "Aug 3" for the axis/tooltip. */
function shortDate(iso: string) {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function CoverageTooltip({ active, payload, label, goalPct }: any) {
  if (!active || !payload?.length) {
    return null;
  }
  const byKey: Record<string, number> = {};
  for (const p of payload) {
    byKey[p.dataKey] = p.value;
  }
  return (
    <div className="rounded-lg border border-zinc-950/10 bg-white px-3 py-2 text-xs shadow-md dark:border-white/10 dark:bg-zinc-900">
      <div className="mb-1 font-medium text-zinc-950 dark:text-white">{shortDate(label)}</div>
      <div className="flex items-center gap-1.5" style={{ color: EMERALD }}>
        <span className="inline-block h-2 w-2 rounded-full" style={{ background: EMERALD }} />
        <span className="text-zinc-600 dark:text-zinc-300">Coverage</span>
        <span className="ml-auto font-semibold">{byKey.pct?.toFixed(1)}%</span>
      </div>
      <div className="flex items-center gap-1.5" style={{ color: INDIGO }}>
        <span className="inline-block h-2 w-2 rounded-full" style={{ background: INDIGO }} />
        <span className="text-zinc-600 dark:text-zinc-300">Reachable</span>
        <span className="ml-auto font-semibold">{byKey.reachable}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="inline-block h-2 w-2 rounded-full" style={{ background: ZINC }} />
        <span className="text-zinc-600 dark:text-zinc-300">Active subs</span>
        <span className="ml-auto font-semibold text-zinc-950 dark:text-white">{byKey.total}</span>
      </div>
      <div className="mt-1 border-t border-zinc-950/10 pt-1 text-[11px] text-zinc-400 dark:border-white/10">
        {byKey.total > 0 ? byKey.total - byKey.reachable : 0} unreachable · goal {goalPct}%
      </div>
    </div>
  );
}

function LegendSwatch({
  color,
  dashed = false,
  label,
}: { color: string; dashed?: boolean; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-zinc-500 dark:text-zinc-400">
      <span
        className="inline-block h-0.5 w-4 rounded"
        style={
          dashed
            ? {
                backgroundImage: `repeating-linear-gradient(90deg, ${color} 0 3px, transparent 3px 6px)`,
              }
            : { background: color }
        }
      />
      {label}
    </span>
  );
}

// Container for the "Trend" tab: fetches the history and renders the bare body.
// No card chrome of its own — it lives inside the Delivery coverage box as a tab
// (#1473), so the box supplies the card and title.
export function CoverageTrendTab({ days = 90 }: { days?: number }) {
  const { history, loading } = useCoverageHistory(days);
  return <CoverageTrendBody history={history} loading={loading} />;
}

// Presentational chart — no card, no title (the box supplies those). Split from
// the container so it can be exercised with fixed data (tests/previews) without
// the network.
export function CoverageTrendBody({
  history,
  loading,
}: {
  history: API.CoverageHistory | null;
  loading: boolean;
}) {
  const chart = useMemo(
    () =>
      (history?.snapshots ?? []).map((s) => ({
        date: s.date,
        pct: Math.round((Number(s.pct) || 0) * 1000) / 10, // 0..1 → 0..100, 1 dp
        reachable: Number(s.reachable) || 0,
        total: Number(s.total) || 0,
      })),
    [history?.snapshots],
  );

  const goalPct = Math.round((history?.goal ?? 0.9) * 100);

  // Left (%) axis: keep the 90% goal line and the working band both visible —
  // clamp the floor a little below the lowest observed %, never above ~80.
  const pctDomain = useMemo<[number, number]>(() => {
    if (!chart.length) {
      return [0, 100];
    }
    const minPct = Math.min(...chart.map((c) => c.pct), goalPct);
    const floor = Math.max(0, Math.min(80, Math.floor((minPct - 5) / 5) * 5));
    return [floor, 100];
  }, [chart, goalPct]);

  // Even 5-point ticks across the % band (recharts' auto ticks come out uneven);
  // the floor..100 grid lands the goal line exactly on a gridline.
  const pctTicks = useMemo(() => {
    const ticks: number[] = [];
    for (let t = pctDomain[0]; t <= 100; t += 5) {
      ticks.push(t);
    }
    return ticks;
  }, [pctDomain]);

  // Right (count) axis: zero-based so the Reachable/Active-subs gap is honest.
  const countMax = useMemo(() => {
    if (!chart.length) {
      return 10;
    }
    const hi = Math.max(...chart.map((c) => c.total));
    const step = 10 ** Math.max(1, Math.floor(Math.log10(hi || 1)));
    return Math.ceil((hi * 1.1) / step) * step;
  }, [chart]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <p className="text-xs text-zinc-500 dark:text-zinc-400">
          Coverage % and the absolute reachable count over time · goal {goalPct}%
        </p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <LegendSwatch color={EMERALD} label="Coverage %" />
          <LegendSwatch color={INDIGO} label="Reachable" />
          <LegendSwatch color={ZINC} label="Active subs" />
          <LegendSwatch color={ZINC} dashed label={`goal ${goalPct}%`} />
        </div>
      </div>

      {loading ? (
        <div className="mt-4 h-64 animate-pulse rounded-lg bg-zinc-50 dark:bg-zinc-800/30" />
      ) : !chart.length ? (
        <div className="mt-4 flex h-64 items-center justify-center text-sm text-zinc-400">
          No coverage history yet — the daily snapshot has not run (or been backfilled).
        </div>
      ) : (
        <div className="mt-4 h-64 w-full min-w-0 text-zinc-500 dark:text-zinc-400">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="currentColor" strokeOpacity={0.12} />
              <XAxis
                dataKey="date"
                tickFormatter={shortDate}
                tickLine={false}
                axisLine={false}
                minTickGap={40}
                interval="preserveStartEnd"
                tick={{ fill: 'currentColor', fontSize: 12 }}
              />
              {/* Left: coverage % */}
              <YAxis
                yAxisId="pct"
                domain={pctDomain}
                ticks={pctTicks}
                tickFormatter={(v: number) => `${v}%`}
                tickLine={false}
                axisLine={false}
                width={44}
                tick={{ fill: 'currentColor', fontSize: 12 }}
              />
              {/* Right: absolute counts (zero-based) */}
              <YAxis
                yAxisId="count"
                orientation="right"
                domain={[0, countMax]}
                tickLine={false}
                axisLine={false}
                width={44}
                allowDecimals={false}
                tick={{ fill: 'currentColor', fontSize: 12 }}
              />
              <Tooltip
                cursor={{ stroke: '#d1d5db', strokeWidth: 1 }}
                content={(props) => <CoverageTooltip {...props} goalPct={goalPct} />}
              />
              {/* 90% goal line on the % axis — the ONLY dashed line, so it never
                  reads as one of the data series. */}
              <ReferenceLine
                yAxisId="pct"
                y={goalPct}
                stroke="currentColor"
                strokeOpacity={0.5}
                strokeDasharray="5 4"
                label={{
                  value: `goal ${goalPct}%`,
                  position: 'insideTopLeft',
                  fill: 'currentColor',
                  fontSize: 11,
                  opacity: 0.7,
                }}
              />
              {/* Total active subs (the base) — faint solid, drawn first (behind).
                  Solid (not dashed) so it's distinct from the dashed goal line. */}
              <Line
                yAxisId="count"
                type="monotone"
                dataKey="total"
                stroke={ZINC}
                strokeWidth={1.5}
                strokeOpacity={0.8}
                dot={false}
                isAnimationActive={false}
              />
              {/* Reachable (absolute) */}
              <Line
                yAxisId="count"
                type="monotone"
                dataKey="reachable"
                stroke={INDIGO}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
                activeDot={{ r: 4 }}
              />
              {/* Coverage % — the headline, drawn last (on top) */}
              <Line
                yAxisId="pct"
                type="monotone"
                dataKey="pct"
                stroke={EMERALD}
                strokeWidth={2.5}
                dot={false}
                isAnimationActive={false}
                activeDot={{ r: 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default CoverageTrendTab;
